<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { useBaseStore } from "@/stores/base";
import ModuleLoading from "@/ui/components/loading/moduleLoading.vue";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
import ColorPaletteGenerator from "@/ui/components/colorPalette/ColorPaletteGenerator.vue";
import ColorPaletteColorDO = App.Data.ColorPaletteColorDO;

type PaletteScope = "global" | "personal" | "all";

const props = withDefaults(defineProps<{ scope?: PaletteScope }>(), {
	scope: "all",
});

const route = useRoute();
const router = useRouter();
const uuid = route.params.palette_uuid as string;

const name = ref("");
const userId = ref<number | null>(null);
const colors = ref<ColorPaletteColorDO[]>([]);
const isSaving = ref(false);
const activeIndex = ref<number | null>(null);

const userOptions = [
	{ name: "Global", id: null },
	{ name: "Personal", id: useBaseStore().currentUser?.id },
];

const showOwnerField = computed(() => props.scope === "all");

const api = computed(() =>
	props.scope === "personal"
		? useApiClient("profile")
		: useApiClient("environment")
);

const activeColor = computed(() =>
	activeIndex.value !== null ? colors.value[activeIndex.value] : null
);

const loadPalette = async () => {
	const response = await api.value.getColorPalette(uuid);
	if (response.isOk()) {
		name.value = response.value.name;
		userId.value = response.value.user_id;
		colors.value = [...(response.value.colors as ColorPaletteColorDO[])];
		activeIndex.value = 0;
	}
};

onBeforeMount(() => loadPalette());

const save = async () => {
	isSaving.value = true;
	const response = await toastApiResult(
		api.value.updateColorPalette({
			uuid,
			name: name.value,
			is_default: false,
			user_id: userId.value,
			colors: colors.value,
		})
	);
	if (response.isOk()) {
		router.back();
	}
	isSaving.value = false;
};

const updateWholePalette = (hexColors: string[]) => {
	hexColors.forEach((hex, i) => {
		if (colors.value[i]) {
			colors.value[i].hex_value = hex || null;
		}
	});
};
</script>

<template>
	<module-loading>
		<v-card class="mt-5" elevation="1">
			<v-toolbar flat class="px-2">
				<v-btn icon variant="text" @click="router.back()">
					<v-icon>mdi-arrow-left</v-icon>
				</v-btn>
				<v-toolbar-title>Edit Color Palette</v-toolbar-title>
			</v-toolbar>

			<v-divider />

			<div class="palette-meta pa-5">
				<v-row>
					<v-col cols="12" md="5">
						<label class="text-caption text-medium-emphasis d-block mb-1"
							>NAME</label
						>
						<v-text-field
							v-model="name"
							variant="filled"
							hide-details
							placeholder="Palette name"
							density="comfortable"
						/>
					</v-col>
					<v-col v-if="showOwnerField" cols="12" md="3">
						<label class="text-caption text-medium-emphasis d-block mb-1"
							>OWNER</label
						>
						<v-select
							v-model="userId"
							:items="userOptions"
							item-title="name"
							item-value="id"
							variant="filled"
							hide-details
							density="comfortable"
						/>
					</v-col>
				</v-row>
			</div>

			<v-divider />

			<div class="editor-body">
				<!-- Left: swatch grid -->
				<div class="swatch-panel pa-5">
					<div class="d-flex align-center mb-4">
						<p class="text-caption text-medium-emphasis mb-0">
							Click a swatch to edit
						</p>
						<v-spacer />
						<color-palette-generator
							:current-colors="colors.map((c) => c.hex_value)"
							@update:palette="updateWholePalette"
						/>
					</div>
					<div class="swatch-grid">
						<div
							v-for="(color, i) in colors"
							:key="color.uuid"
							class="swatch"
							:class="{
								'swatch--empty': !color.hex_value,
								'swatch--active': activeIndex === i,
							}"
							:style="
								color.hex_value ? { backgroundColor: color.hex_value } : {}
							"
							@click="activeIndex = i"
						/>
					</div>
					<p class="text-caption text-medium-emphasis mt-2">
						Colors from the palette are applied in order—moving left to right,
						then top to bottom.
					</p>
				</div>

				<v-divider vertical />

				<!-- Right: detail panel -->
				<div class="detail-panel pa-5">
					<template v-if="activeColor">
						<div class="d-flex">
							<div>
								<p class="text-caption text-medium-emphasis mb-4">
									SLOT {{ (activeIndex ?? 0) + 1 }} OF {{ colors.length }}
								</p>
							</div>
							<v-spacer />
							<v-btn
								style="margin-top: -4px"
								size="small"
								variant="text"
								color="error"
								@click="activeColor.hex_value = null"
							>
								Clear slot
							</v-btn>
						</div>

						<color-picker
							inline
							:model-value="activeColor.hex_value ?? '#FFFFFF'"
							@color-updated="(val: string) => (activeColor.hex_value = val)"
						/>

						<div class="d-flex"></div>
					</template>

					<div v-else class="empty-state">
						<v-icon size="40" color="grey-lighten-2" class="mb-3"
							>mdi-palette-outline</v-icon
						>
						<p class="text-body-2 text-medium-emphasis text-center">
							Click any swatch on the left<br />to edit its color
						</p>
					</div>
				</div>
			</div>
			<v-divider class="mb-1"></v-divider>
			<v-card-actions>
				<v-spacer />
				<v-btn variant="text" class="mr-2" @click="router.back()">
					Cancel
				</v-btn>
				<v-btn color="primary" variant="flat" :loading="isSaving" @click="save">
					Save
				</v-btn>
			</v-card-actions>
		</v-card>
	</module-loading>
</template>

<style scoped lang="scss">
.editor-body {
	display: flex;
	min-height: 340px;
}

.swatch-panel {
	flex-shrink: 0;
}

.swatch-grid {
	display: grid;
	grid-template-columns: repeat(20, 28px);
	gap: 3px;
}

.swatch {
	width: 28px;
	height: 28px;
	border-radius: 4px;
	cursor: pointer;
	transition: box-shadow 0.1s ease;

	&:hover {
		box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
	}

	&--active {
		box-shadow: 0 0 0 2px #fff, 0 0 0 4px rgb(var(--v-theme-primary));
	}

	&--empty {
		background-color: #eeeeee;
		border: 1px solid #d5d5d5;
	}
}

.detail-panel {
	flex: 1;
	display: flex;
	flex-direction: column;
}

.empty-state {
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
}
</style>
